
import React, { useState } from "react";
import { MessageSquarePlus, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ClarificationInput from "../ClarificationInput";
import type { ClarificationType } from "../ClarificationInput";

interface FloatingClarificationButtonProps {
  onClarificationSubmit: (clarification: string, type: ClarificationType) => void;
  isSubmitting?: boolean;
  hasAIMessages?: boolean;
}

const FloatingClarificationButton = ({ 
  onClarificationSubmit, 
  isSubmitting = false,
  hasAIMessages = false
}: FloatingClarificationButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Show the button if there are AI messages
  if (!hasAIMessages) {
    console.log("FloatingClarificationButton: Not showing because hasAIMessages is", hasAIMessages);
    return null;
  }

  console.log("FloatingClarificationButton: Showing button, hasAIMessages:", hasAIMessages);

  const handleClarificationSubmit = (clarification: string, type: ClarificationType) => {
    onClarificationSubmit(clarification, type);
    setIsOpen(false); // Close dialog after submission
  };

  const suggestions = [
    "Make the tone more aggressive and demanding",
    "Add legal language about consumer protection laws", 
    "Include specific warranty or return policy details",
    "Mention potential chargeback or dispute filing",
    "Make it sound more friendly and understanding",
    "Focus on the business relationship and future purchases"
  ];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          size="lg"
          className="fixed bottom-6 right-6 h-16 px-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-50 bg-primary hover:bg-primary/90 text-primary-foreground flex items-center gap-3"
          title="Refine Your Request"
        >
          <Wand2 className="h-5 w-5" />
          <span className="text-sm font-medium">Refine Your Request</span>
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <MessageSquarePlus className="h-5 w-5 text-primary" />
            Refine Your AI Response
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-2">
          <ClarificationInput
            onSubmitClarification={handleClarificationSubmit}
            isSubmitting={isSubmitting}
            suggestions={suggestions}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FloatingClarificationButton;
